import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ConfigProvider,
  Drawer,
  Input,
  Progress,
  Result,
  Spin,
  Table,
  Tag,
  message,
  theme as antdTheme,
} from 'antd';
import {
  RollbackOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '@/stores/userStore';
import {
  certificateLabels,
  crewProfileListAdmin,
  type CrewCertificate,
  type CrewProfile,
  type CrewProfileAdminItem,
  type ProductManagementRecord,
} from '@/services/crewProfile';
import styles from './CrewProfile.module.css';

const displayName = (item: CrewProfileAdminItem) =>
  item.realName || item.username || item.email || item.adenId;

const formatDate = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD') : '-');

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-';

const renderCertificates = (profile: CrewProfile | null) => {
  if (!profile || ((profile.certificates?.length ?? 0) === 0 && !profile.certificateOther)) {
    return <span className={styles.emptyText}>未维护</span>;
  }

  const tags = (profile.certificates ?? []).map((cert: CrewCertificate) => {
    const label = cert === 'other' && profile.certificateOther
      ? profile.certificateOther
      : certificateLabels[cert];
    return <Tag key={cert} color="cyan">{label}</Tag>;
  });

  if (profile.certificateOther && !profile.certificates?.includes('other')) {
    tags.push(<Tag key="other-text" color="cyan">{profile.certificateOther}</Tag>);
  }

  return <div className={styles.tagList}>{tags}</div>;
};

const renderProductRecords = (records: ProductManagementRecord[] | undefined) => {
  if (!records || records.length === 0) {
    return <span className={styles.emptyText}>未维护</span>;
  }

  return (
    <div className={styles.recordsList}>
      {records.map((record, index) => (
        <div key={`${record.year}-${index}`} className={styles.recordItem}>
          <span>{record.year}</span>
          <span>{record.scale}</span>
          <span>{record.returnRate ?? '-'}%</span>
        </div>
      ))}
    </div>
  );
};

function CrewProfileAdmin() {
  const navigate = useNavigate();
  const isAdmin = useUserStore((s) => s.isAdmin);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<CrewProfileAdminItem[]>([]);
  const [keyword, setKeyword] = useState('');
  const [selected, setSelected] = useState<CrewProfileAdminItem | null>(null);

  const load = () => {
    setLoading(true);
    crewProfileListAdmin()
      .then(setItems)
      .catch(() => message.error('加载机组信息失败'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  const filteredItems = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => [
      displayName(item),
      item.adenId,
      item.username,
      item.email,
      item.phone ?? '',
      item.profile?.phone ?? '',
    ].some((value) => value.toLowerCase().includes(q)));
  }, [items, keyword]);

  const columns: ColumnsType<CrewProfileAdminItem> = [
    {
      title: '机组',
      key: 'crew',
      fixed: 'left',
      width: 220,
      render: (_, item) => (
        <div className={styles.nameCell}>
          <span className={styles.nameMain}>{displayName(item)}</span>
          <span className={styles.nameSub}>ID · {item.adenId}</span>
        </div>
      ),
    },
    {
      title: '联系电话',
      key: 'phone',
      width: 150,
      render: (_, item) => item.profile?.phone || item.phone || '-',
    },
    {
      title: '信息完善程度',
      key: 'completion',
      width: 190,
      render: (_, item) => (
        <Progress
          percent={item.completion.percent}
          size="small"
          status={item.completion.percent === 100 ? 'success' : 'active'}
        />
      ),
    },
    {
      title: '必填项',
      key: 'required',
      width: 210,
      render: (_, item) => {
        const missing = item.completion.missingRequiredFields;
        if (missing.length === 0) return <Tag color="green">已齐全</Tag>;
        return (
          <div className={styles.tagList}>
            <Tag color="orange">缺 {missing.length} 项</Tag>
            {missing.slice(0, 2).map((field) => (
              <Tag key={field}>{field}</Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: '资质证书',
      key: 'certificates',
      width: 260,
      render: (_, item) => renderCertificates(item.profile),
    },
    {
      title: '产品管理',
      key: 'products',
      width: 120,
      render: (_, item) => `${item.profile?.productManagementRecords?.length ?? 0} 条`,
    },
    {
      title: '更新时间',
      key: 'updatedAt',
      width: 170,
      render: (_, item) => formatDateTime(item.updatedAt),
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 110,
      render: (_, item) => (
        <Button size="small" className={styles.ghostBtn} onClick={() => setSelected(item)}>
          查看
        </Button>
      ),
    },
  ];

  if (!isAdmin) {
    return (
      <Result
        status="403"
        title="403"
        subTitle="抱歉，仅产品管理员可访问星舰机组管理。"
        extra={
          <Button type="primary" onClick={() => navigate('/dashboard')}>
            返回驾驶舱
          </Button>
        }
      />
    );
  }

  return (
    <ConfigProvider
      theme={{
        algorithm: antdTheme.darkAlgorithm,
        token: {
          colorPrimary: '#00ffff',
          colorInfo: '#00ffff',
          colorBgContainer: 'rgba(10, 22, 44, 0.86)',
          colorBgElevated: 'rgba(10, 22, 44, 0.96)',
          colorBorder: 'rgba(0, 255, 255, 0.18)',
          colorBorderSecondary: 'rgba(0, 255, 255, 0.12)',
          fontFamily: "'PingFang SC', 'Microsoft YaHei', sans-serif",
          borderRadius: 4,
        },
      }}
    >
      <div className={styles.page}>
        <div className={styles.starfield} />
        <div className={styles.gridBackground} />
        <span className={`${styles.hudCorner} ${styles.tl}`} />
        <span className={`${styles.hudCorner} ${styles.tr}`} />
        <span className={`${styles.hudCorner} ${styles.bl}`} />
        <span className={`${styles.hudCorner} ${styles.br}`} />

        <div className={`${styles.container} ${styles.wideContainer}`}>
          <div className={styles.header}>
            <div className={styles.titleGroup}>
              <span className={styles.crumb}>AdenCockpit / CREW MANAGEMENT</span>
              <span className={styles.title}>
                <TeamOutlined className={styles.titleIcon} />
                星舰机组管理
              </span>
              <span className={styles.subtitle}>CREW · PROFILE · COMPLETION</span>
            </div>
            <Button
              icon={<RollbackOutlined />}
              className={styles.backBtn}
              onClick={() => navigate('/dashboard')}
            >
              返回驾驶舱
            </Button>
          </div>

          <div className={styles.panel}>
            <div className={styles.adminToolbar}>
              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="搜索姓名、AdenID、邮箱或电话"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                style={{ maxWidth: 360 }}
              />
              <Button className={styles.ghostBtn} onClick={load}>
                刷新
              </Button>
            </div>

            <div className={styles.tablePanel}>
              <Spin spinning={loading}>
                <Table
                  rowKey="adenId"
                  columns={columns}
                  dataSource={filteredItems}
                  pagination={{ pageSize: 10, showSizeChanger: true }}
                  scroll={{ x: 1420 }}
                />
              </Spin>
            </div>
          </div>
        </div>

        <Drawer
          title={selected ? `${displayName(selected)} · 机组信息` : '机组信息'}
          open={!!selected}
          width={720}
          onClose={() => setSelected(null)}
        >
          {selected && (
            <>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>姓名</div>
                  <div className={styles.detailValue}>{displayName(selected)}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>AdenID</div>
                  <div className={styles.detailValue}>{selected.adenId}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>邮箱</div>
                  <div className={styles.detailValue}>{selected.email || '-'}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>信息完善程度</div>
                  <Progress percent={selected.completion.percent} size="small" />
                </div>
              </div>

              <div className={styles.sectionTitle}>基础信息</div>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>身份证号码</div>
                  <div className={styles.detailValue}>{selected.profile?.nationalId || '-'}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>生日</div>
                  <div className={styles.detailValue}>
                    {formatDate(selected.profile?.birthday)}
                    {selected.profile?.birthdayCalendar === 'lunar' ? ' · 农历' : ' · 公历'}
                  </div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>入职时间</div>
                  <div className={styles.detailValue}>{formatDate(selected.profile?.hireDate)}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>联系电话</div>
                  <div className={styles.detailValue}>{selected.profile?.phone || selected.phone || '-'}</div>
                </div>
                <div className={`${styles.detailItem} ${styles.detailWide}`}>
                  <div className={styles.detailLabel}>家庭住址</div>
                  <div className={styles.detailValue}>{selected.profile?.homeAddress || '-'}</div>
                </div>
              </div>

              <div className={styles.sectionTitle}>紧急联系人</div>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>姓名</div>
                  <div className={styles.detailValue}>{selected.profile?.emergencyContact?.name || '-'}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>电话</div>
                  <div className={styles.detailValue}>{selected.profile?.emergencyContact?.phone || '-'}</div>
                </div>
              </div>

              <div className={styles.sectionTitle}>资质与健康</div>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>上次体检时间</div>
                  <div className={styles.detailValue}>{formatDate(selected.profile?.lastMedicalCheckDate)}</div>
                </div>
                <div className={styles.detailItem}>
                  <div className={styles.detailLabel}>资质证书</div>
                  <div className={styles.detailValue}>{renderCertificates(selected.profile)}</div>
                </div>
              </div>

              <div className={styles.sectionTitle}>产品管理情况</div>
              {renderProductRecords(selected.profile?.productManagementRecords)}
            </>
          )}
        </Drawer>
      </div>
    </ConfigProvider>
  );
}

export default CrewProfileAdmin;
